import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, apiError, downloadFile } from "@/lib/api";
import { fmtNumber, fmtDate } from "@/lib/format";
import { Download, Upload, CheckCircle2, XCircle, AlertCircle, FileSpreadsheet } from "lucide-react";
import HelpButton from "@/components/HelpButton";
import { toast } from "sonner";

function StatusBadge({ status }) {
  if (status === "valid") return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 gap-1"><CheckCircle2 className="h-3 w-3" />valid</Badge>;
  if (status === "warning") return <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100 gap-1"><AlertCircle className="h-3 w-3" />avertisment</Badge>;
  return <Badge className="bg-red-100 text-red-700 hover:bg-red-100 gap-1"><XCircle className="h-3 w-3" />eroare</Badge>;
}

// config: { title, templatePath, templateName, validatePath, confirmPath, previewBase, columns:[[key,label,type]], modes:[[value,label]] }
export default function SimpleImportWizard({ open, onOpenChange, onDone, config }) {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState(config.modes[0][0]);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onlyIssues, setOnlyIssues] = useState(true);

  const reset = () => { setStep(1); setMode(config.modes[0][0]); setFile(null); setPreview(null); setBusy(false); setOnlyIssues(true); };
  const close = (v) => { if (!v) reset(); onOpenChange(v); };

  const doTemplate = async () => {
    try { await downloadFile(config.templatePath, config.templateName); }
    catch (e) { toast.error("Nu s-a putut descărca șablonul"); }
  };
  const doValidate = async () => {
    if (!file) { toast.error("Selectați un fișier"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mode", mode);
      const { data } = await api.post(config.validatePath, fd);
      setPreview(data);
      setStep(3);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Validare eșuată"); }
    finally { setBusy(false); }
  };
  const doConfirm = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("import_id", preview.import_id);
      const { data } = await api.post(config.confirmPath, fd);
      toast.success(`Import finalizat: ${data.written} înregistrări`);
      close(false);
      onDone?.();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Confirmare eșuată"); }
    finally { setBusy(false); }
  };
  const doDownloadPreview = async () => {
    try { await downloadFile(`${config.previewBase}/${preview.import_id}/export?only_issues=${onlyIssues}`, "Previzualizare_import.xlsx"); }
    catch (e) { toast.error("Descărcare eșuată"); }
  };

  const steps = ["Descarcă șablon", "Încarcă fișier", "Previzualizare", "Confirmă"];
  const rows = preview ? (onlyIssues ? preview.issues : preview.sample) : [];

  const fmtCell = (r, c) => {
    const v = r[c[0]];
    if (c[2] === "num") return fmtNumber(v);
    if (c[2] === "date") return fmtDate(v);
    return v;
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="prepaid-import-wizard">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-[#FFC800]" /> {config.title}
            {config.helpAnchor && <HelpButton anchor={config.helpAnchor} className="ml-auto" />}
          </DialogTitle>
          <DialogDescription className="sr-only">Asistent import în 4 pași.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 text-xs mb-2">
          {steps.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span className={`px-2 py-1 rounded ${step === i + 1 ? "bg-slate-900 text-white" : step > i + 1 ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{i + 1}. {s}</span>
              {i < steps.length - 1 && <span className="text-slate-300">→</span>}
            </div>
          ))}
        </div>

        <div className="overflow-auto flex-1">
          {step === 1 && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-slate-600">Descărcați șablonul cu structura exactă și completați-l.</p>
              <Button data-testid="download-template-btn" onClick={doTemplate} variant="outline" className="gap-2"><Download className="h-4 w-4" /> Descarcă șablon .xlsx</Button>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Mod import</label>
                <Select value={mode} onValueChange={setMode}>
                  <SelectTrigger className="w-80" data-testid="import-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{config.modes.map((m) => <SelectItem key={m[0]} value={m[0]}>{m[1]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Fișier Excel</label>
                <input type="file" accept=".xlsx" data-testid="import-file-input" onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-slate-900 file:text-white file:cursor-pointer" />
                {file && <p className="text-xs text-slate-500">Selectat: {file.name}</p>}
              </div>
            </div>
          )}
          {step === 3 && preview && (
            <div className="space-y-3 py-2">
              <div className="flex flex-wrap items-center gap-3 justify-between border-b pb-3">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-1 rounded bg-slate-100 text-slate-700">Citite: {preview.summary.total}</span>
                  <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700">Valide: {preview.summary.valid}</span>
                  <span className="px-2 py-1 rounded bg-amber-50 text-amber-700">Avertismente: {preview.summary.warnings}</span>
                  <span className="px-2 py-1 rounded bg-red-50 text-red-700">Erori: {preview.summary.errors}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Switch checked={onlyIssues} onCheckedChange={setOnlyIssues} data-testid="only-issues-toggle" />
                    <span className="text-sm">Doar erori și avertismente</span>
                  </div>
                  <Button variant="outline" size="sm" onClick={doDownloadPreview} className="gap-2" data-testid="download-preview-btn"><Download className="h-4 w-4" /> Descarcă</Button>
                </div>
              </div>
              {onlyIssues && rows.length === 0 && (
                <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2">Nicio eroare sau avertisment.</div>
              )}
              {rows.length > 0 && (
                <div className="border rounded max-h-72 overflow-auto">
                  <table className="w-full text-xs jh-table">
                    <thead className="sticky top-0 bg-slate-100">
                      <tr>
                        <th className="text-left px-2 py-1.5">Rând</th>
                        {config.columns.map((c) => <th key={c[0]} className={`px-2 py-1.5 ${c[2] === "num" ? "text-right" : "text-left"}`}>{c[1]}</th>)}
                        <th className="text-left px-2 py-1.5">Status</th>
                        <th className="text-left px-2 py-1.5">Motiv</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-2 py-1 tabular">{r.row}</td>
                          {config.columns.map((c) => <td key={c[0]} className={`px-2 py-1 ${c[2] === "num" ? "text-right tabular" : ""}`}>{fmtCell(r, c)}</td>)}
                          <td className="px-2 py-1"><StatusBadge status={r.status} /></td>
                          <td className="px-2 py-1 text-slate-500">{r.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {preview.has_error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">Există rânduri cu erori. Confirmarea este blocată.</div>}
            </div>
          )}
        </div>

        <div className="flex justify-between pt-3 border-t">
          <Button variant="ghost" onClick={() => close(false)} data-testid="wizard-cancel-btn">Anulează</Button>
          <div className="flex gap-2">
            {step > 1 && step < 4 && <Button variant="outline" onClick={() => setStep(step - 1)} disabled={busy}>Înapoi</Button>}
            {step === 1 && <Button onClick={() => setStep(2)} className="bg-slate-900 text-white" data-testid="wizard-next-btn">Continuă</Button>}
            {step === 2 && <Button onClick={doValidate} disabled={busy || !file} className="bg-slate-900 text-white gap-2" data-testid="wizard-validate-btn"><Upload className="h-4 w-4" /> {busy ? "Se validează…" : "Validează"}</Button>}
            {step === 3 && <Button onClick={doConfirm} disabled={busy || preview?.has_error} className="bg-[#FFC800] text-slate-950 hover:bg-[#E6B200] font-semibold" data-testid="wizard-confirm-btn">{busy ? "Se salvează…" : "Confirmă import"}</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
